import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { RoleE } from "shared/api/types/auth";
import { UsersElementT } from "shared/api/types/users";
import CutText from "shared/components/CutText";
import MainItemMenu from "shared/components/MainItemMenu";
import { PathsE } from "shared/routes";
import { FilterField } from "shared/types/filters";
import { formatDateToHuman } from "shared/utils/formattings";
import { Center, Checkbox, Text } from "@chakra-ui/react";
import Link from "../../components/Link";


export const roleOptions: { value: RoleE; label: string }[] = [
  {
    value: RoleE.ADMIN,
    label: "Admin",
  },
  {
    value: RoleE.MANAGER,
    label: "Manager",
  },
];

export const isDeletedOptions: { value: string; label: string }[] = [
  {
    value: "true",
    label: "true",
  },
  {
    value: "false",
    label: "false",
  },
];

export const useGetFilterFields = (): FilterField[] => {
  const { t } = useTranslation();

  return useMemo(
    () => [
      {
        type: "select",
        isMulti: true,
        name: "role",
        defaultValue: [],
        placeholder: t("forms.role"),
        options: roleOptions,
        label: t("forms.role"),
        size: "lg",
      },
      {
        type: "text",
        name: "surname",
        defaultValue: "",
        placeholder: t("forms.surname"),
        label: t("forms.surname"),
        size: "lg",
      },
      {
        type: "text",
        name: "name",
        defaultValue: "",
        placeholder: t("forms.name"),
        label: t("forms.name"),
        size: "lg",
      },
      {
        type: "text",
        name: "email",
        defaultValue: "",
        placeholder: t("forms.email"),
        label: t("forms.email"),
        size: "lg",
      },
      {
        type: "date-range",
        name: "createdAt",
        defaultValue: [],
        label: t("users.table.createdAt"),
        size: "lg",
      },
      {
        type: "select",
        name: "isDeleted",
        defaultValue: "false",
        label: t("users.table.showDelete"),
        placeholder: t("users.table.showDelete"),
        options: isDeletedOptions,
        size: "lg",
      },
    ],
    [t],
  );
};

export const useGetGetBreadcrumbs = () => {
  const { t } = useTranslation();
  return [
    {
      path: PathsE.HOME,
      text: t("general.home"),
    },
  ];
};

interface Params {
  handleActivate(userId: string): void;
  handleDeactivate(userId: string): void;
  handleDelete(id: string): void;
  handleRestore(id: string): void;
}

export const useGetColumns = ({
  handleActivate,
  handleDeactivate,
  handleDelete,
  handleRestore,
}: Params) => {
  const { t } = useTranslation();
  const columnHelper = createColumnHelper<UsersElementT>();

  return useMemo(
    () =>
      [
        columnHelper.accessor("id", {
          header: t("users.table.id"),
          id: "id",
          cell: ({ getValue }) => (
            <CutText fontWeight="700">{getValue()}</CutText>
          ),
          meta: {
            isSorting: false,
            styles: {
              sizes: {
                width: "90px",
                maxW: "90px",
              },
            },
          },
        }),
        columnHelper.accessor("email", {
          cell: ({ row, getValue }) => (
              <Link path={PathsE.UPDATE_USER} pathsParams={{ userId: row.original.id }}>
                <Text color="blue">{getValue()}</Text>
              </Link>
          ),
          header: t("users.table.email"),
          id: "email",
        }),
        columnHelper.accessor("name", {
          header: t("users.table.name"),
          id: "name",
        }),
        columnHelper.accessor("surname", {
          header: t("users.table.surname"),
          id: "surname",
        }),
        columnHelper.accessor("role", {
          header: t("users.table.role"),
          id: "role",
        }),
        columnHelper.accessor("isActive", {
          header: t("channels.table.isActive"),
          id: "isActive",
          meta: {
            styles: {
              sizes: {
                width: "70px",
              },
            },
          },
          cell: ({ row }) => {
            const { id, isActive } = row.original;

            return (
              <Center>
                <Checkbox
                  isChecked={isActive}
                  onChange={() => handleDeactivate(id)}
                  colorScheme="brand"
                />
              </Center>
            );
          },
        }),
        columnHelper.accessor("createdAt", {
          header: t("users.table.createdAt"),
          id: "createdAt",
          cell: ({ getValue }) => formatDateToHuman(getValue(), false),
          meta: {
            isNumeric: true,
            styles: {
              sizes: {
                width: "120px",
              },
            },
          },
        }),
        columnHelper.accessor("updatedAt", {
          header: t("users.table.updatedAt"),
          id: "updatedAt",
          cell: ({ getValue }) => formatDateToHuman(getValue(), false),
          meta: {
            isNumeric: true,
            styles: {
              sizes: {
                width: "120px",
              },
            },
          },
        }),
        columnHelper.accessor("id", {
          header: "",
          id: "actions",
          cell: ({ row }) => (
            <MainItemMenu
              data={row.original}
              handleActivate={handleActivate}
              handleDeactivate={handleDeactivate}
              handleDelete={handleDelete}
              handleRestore={handleRestore}
              isDeleted={row.original.isDeleted}
              editLink={PathsE.UPDATE_USER}
              pathsParams={{ userId: row.original.id }}
            />
          ),
          meta: {
            styles: {
              sizes: {
                width: "44px",
              },
            },
          },
        }),
      ] as ColumnDef<UsersElementT>[],
    [t, handleActivate, handleDeactivate, handleDelete],
  );
};
